import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaWhatsapp } from 'react-icons/fa';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { BADGES, GAMES, formatINR } from '../../../data/games';
import { ROUTES, WA_MESSAGES, openWhatsApp } from '../../../config/site';

const FEATURED = GAMES.filter((g) => g.isPopular).slice(0, 6);

const LiveGamesPreview = () => {
  const [live, setLive] = useState(() =>
    Object.fromEntries(FEATURED.map((g) => [g.id, g.playerCount]))
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setLive((prev) => {
        const next = { ...prev };
        FEATURED.forEach((g) => {
          const drift = Math.floor(Math.random() * 24) - 12;
          next[g.id] = Math.max(120, (next[g.id] ?? g.playerCount) + drift);
        });
        return next;
      });
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative overflow-hidden py-24">
      <span className="aurora left-[-6%] top-[10%] h-96 w-96 bg-royal-violet/30" aria-hidden="true" />
      <span className="aurora right-[-8%] bottom-[6%] h-96 w-96 bg-royal-gold/25" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="mx-auto max-w-3xl text-center">
          <span className="badge-pill">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-victory-red opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-victory-red" />
            </span>
            Live right now
          </span>
          <h2 className="mt-6 font-heading text-display font-bold text-white">
            Tables are <span className="gold-text">open</span> and dealing
          </h2>
          <p className="mt-4 text-body-lg text-white/60">
            Studio dealers, verified shoes and instant WhatsApp booking. Pick a table, message us, and your seat is
            confirmed before the next hand is dealt.
          </p>
        </div>

        {/* Live counter strip */}
        <div className="panel-gold mt-12 grid grid-cols-2 gap-6 px-6 py-7 sm:grid-cols-4">
          {[
            { value: '8,547', label: 'Players seated now', icon: 'Users', color: '#5B8CFF' },
            { value: '₹2.3Cr', label: "Won in the last 24h", icon: 'TrendingUp', color: '#25D366' },
            { value: '156', label: 'Winners this hour', icon: 'Trophy', color: '#F0C24B' },
            { value: '4.9★', label: 'Average player rating', icon: 'Star', color: '#FF8ED0' },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <span
                className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10"
                style={{ background: `${s.color}1F` }}
              >
                <Icon name={s.icon} size={20} color={s.color} />
              </span>
              <div className="font-display text-2xl font-bold text-white">{s.value}</div>
              <div className="mt-1 text-xs text-white/45">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Game grid */}
        <div className="perspective mt-14 grid gap-7 md:grid-cols-2 lg:grid-cols-3">
          {FEATURED.map((game, i) => {
            const badge = BADGES[game.badge] ?? BADGES.classic;
            const players = live[game.id] ?? game.playerCount;

            return (
              <article
                key={game.id}
                className="card-3d group animate-rise overflow-hidden"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                {/* Art */}
                <div className="relative h-48 overflow-hidden">
                  <Image
                    src={game.image}
                    alt={`${game.name} — play on Nitin Online Book`}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#08080F] via-transparent to-transparent" />

                  <span className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full border border-white/15 bg-black/60 px-2.5 py-1 text-[0.62rem] font-bold tracking-wider text-white backdrop-blur">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-victory-red" />
                    LIVE
                  </span>

                  <span
                    className={`absolute right-4 top-4 flex items-center gap-1 rounded-full bg-gradient-to-r ${badge.className} px-2.5 py-1 text-[0.62rem] font-bold text-[#150F02] shadow-[0_8px_18px_-8px_rgba(0,0,0,0.9)]`}
                  >
                    <Icon name={badge.icon} size={11} color="#150F02" />
                    {badge.label}
                  </span>

                  <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
                    <div>
                      <h3 className="font-heading text-xl font-bold text-white drop-shadow">{game.name}</h3>
                      <p className="text-[0.7rem] text-white/55">{game.tagline}</p>
                    </div>
                    <span className="rounded-full border border-white/15 bg-black/50 px-2 py-0.5 text-[0.62rem] text-white/70 backdrop-blur">
                      RTP {game.rtp}
                    </span>
                  </div>
                </div>

                {/* Body */}
                <div className="p-6">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-wa-green">
                      <Icon name="Users" size={15} />
                      <span className="font-semibold tabular-nums">{players.toLocaleString('en-IN')}</span>
                      <span className="text-white/40">playing</span>
                    </span>
                    <span className="rounded-full bg-white/[0.06] px-2.5 py-1 text-[0.68rem] text-white/55">
                      {game.category}
                    </span>
                  </div>

                  <div className="my-5 grid grid-cols-2 gap-3">
                    <div className="rounded-xl border border-white/8 bg-white/[0.04] px-3 py-2.5">
                      <p className="text-[0.62rem] uppercase tracking-[0.16em] text-white/40">Min bet</p>
                      <p className="mt-0.5 font-display font-semibold text-white">{formatINR(game.minBet)}</p>
                    </div>
                    <div className="rounded-xl border border-white/8 bg-white/[0.04] px-3 py-2.5">
                      <p className="text-[0.62rem] uppercase tracking-[0.16em] text-white/40">Max win</p>
                      <p className="mt-0.5 font-display font-semibold gold-text">{formatINR(game.maxWin)}</p>
                    </div>
                  </div>

                  <button
                    onClick={() => openWhatsApp(WA_MESSAGES.game(game.name))}
                    className="btn-3d btn-3d-wa w-full !py-3 text-sm"
                  >
                    <FaWhatsapp size={17} /> Book this table
                  </button>

                  <Link
                    to={ROUTES.games}
                    className="mt-3 flex items-center justify-center gap-1.5 text-xs text-white/45 transition hover:text-royal-gold"
                  >
                    Rules, strategy &amp; limits <Icon name="ArrowUpRight" size={13} />
                  </Link>
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-14 text-center">
          <Link to={ROUTES.games} className="btn-3d btn-3d-ghost">
            Explore all 50+ premium games
            <Icon name="ArrowRight" size={17} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default LiveGamesPreview;
